import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MagnifyingGlass, Plus, User } from "@phosphor-icons/react";

export default function Customers() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", notes: "" });

  const load = async () => {
    const r = await api.get("/customers", { params: q ? { q } : {} });
    setItems(r.data);
  };
  useEffect(() => { load(); /* only on q change */ }, [q]);

  const quotesTotal = useMemo(
    () => (detail?.quotes || []).reduce((s, x) => s + (x.total || 0), 0).toFixed(2),
    [detail]
  );

  const openDetail = async (id) => {
    const r = await api.get(`/customers/${id}`);
    setDetail(r.data);
  };
  const create = async () => {
    if (!form.name.trim()) return;
    await api.post("/customers", form);
    toast.success("Customer created");
    setShowForm(false);
    setForm({ name: "", email: "", phone: "", address: "", notes: "" });
    load();
  };

  return (
    <div className="p-8 max-w-6xl">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.28em] text-neutral-500 dark:text-neutral-500 mb-2">CRM</div>
          <h1 className="text-4xl font-semibold tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>Customers</h1>
        </div>
        <button data-testid="new-customer-btn" onClick={() => setShowForm(true)} className="text-sm px-4 py-2 rounded-md bg-[#0F52BA] text-white hover:bg-[#0C4094] flex items-center gap-2"><Plus size={16} weight="bold" /> New</button>
      </div>

      <div className="relative mb-4 max-w-md">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500 dark:text-neutral-500" />
        <input
          data-testid="customer-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name, email or phone"
          className="w-full pl-9 pr-3 py-2 border border-neutral-300 dark:border-neutral-700 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0F52BA]/40"
        />
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 border border-neutral-200 dark:border-neutral-800 rounded-lg bg-white dark:bg-neutral-900 divide-y divide-neutral-100 dark:divide-neutral-800">
          {items.length === 0 ? (
            <div className="p-8 text-sm text-neutral-500 dark:text-neutral-500 text-center">No customers yet.</div>
          ) : items.map((c) => (
            <button key={c.customer_id} data-testid={`cust-row-${c.customer_id}`} onClick={() => openDetail(c.customer_id)} className="w-full text-left p-4 hover:bg-neutral-50 dark:hover:bg-neutral-800/60 flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center text-neutral-500 dark:text-neutral-500"><User size={16} /></div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{c.name}</div>
                <div className="text-xs text-neutral-500 dark:text-neutral-500 truncate">{c.email || "—"} · {c.phone || "no phone"}</div>
              </div>
            </button>
          ))}
        </div>

        <div className="border border-neutral-200 dark:border-neutral-800 rounded-lg bg-white dark:bg-neutral-900 p-5">
          {!detail ? (
            <div className="text-sm text-neutral-500 dark:text-neutral-500">Select a customer to view history.</div>
          ) : (
            <div>
              <div className="text-lg font-medium" style={{ fontFamily: "Outfit, sans-serif" }}>{detail.name}</div>
              <div className="text-xs text-neutral-500 dark:text-neutral-500">{detail.email} · {detail.phone}</div>
              <div className="mt-4 text-xs uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-500">Conversations</div>
              <div className="text-sm mt-1">{detail.conversations?.length || 0}</div>
              <div className="mt-4 text-xs uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-500">Quotes</div>
              <div className="text-sm mt-1">{detail.quotes?.length || 0} · ${quotesTotal}</div>
              <div className="mt-4 text-xs uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-500">Appointments</div>
              <div className="text-sm mt-1">{detail.appointments?.length || 0}</div>
            </div>
          )}
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-neutral-900 rounded-lg p-6 w-full max-w-md">
            <div className="text-lg font-medium mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>New Customer</div>
            {["name", "email", "phone", "address"].map((k) => (
              <input
                key={k}
                data-testid={`cust-form-${k}`}
                value={form[k]}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                placeholder={k[0].toUpperCase() + k.slice(1)}
                className="w-full mb-2 px-3 py-2 border border-neutral-300 dark:border-neutral-700 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0F52BA]/40"
              />
            ))}
            <textarea
              data-testid="cust-form-notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Notes"
              rows={3}
              className="w-full mb-4 px-3 py-2 border border-neutral-300 dark:border-neutral-700 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0F52BA]/40"
            />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowForm(false)} className="text-sm px-4 py-2 rounded-md border border-neutral-300 dark:border-neutral-700">Cancel</button>
              <button data-testid="cust-form-save" onClick={create} className="text-sm px-4 py-2 rounded-md bg-[#0F52BA] text-white hover:bg-[#0C4094]">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
